import { useMemo } from 'react';

import { hasActiveLeadFilters } from '../constants/leadFilters';
import { useLeadData } from '../contexts/LeadDataContext';
import { useLeadUI } from '../contexts/LeadUIContext';

/**
 * The company scope a credit-affecting /leads bulk action (analysis, enrichment, export,
 * contact list, CRM transfer, delete) targets: an explicit page selection wins; otherwise —
 * "all in filter" or no selection at all — every company of the query matching the active
 * table filters, resolved server-side so it scales to any query size.
 *
 * @returns {{
 *   scope: {leadIds: string[]} | {selectAll: true, filters?: Object},
 *   count: number,
 *   knownLeads: Array<Object>,
 *   prospectQueryId: string|null,
 * }} `scope` spreads into the request body; `count` is the number of companies it covers;
 *   `knownLeads` are the rows of the scope the client holds (the selection, or the loaded page
 *   of an "all in filter" scope) for previews that need per-company data.
 */
export const useActionScope = () => {
	const { selectedSearchQueryId, filters, totalCount, leads } = useLeadData();
	const { selectedLeads, selectAllInFilter } = useLeadUI();

	return useMemo(() => {
		if (selectedLeads.length > 0 && !selectAllInFilter) {
			const selected = new Set(selectedLeads);
			return {
				scope: { leadIds: selectedLeads },
				count: selectedLeads.length,
				knownLeads: leads.filter((lead) => selected.has(lead.id)),
				prospectQueryId: selectedSearchQueryId,
			};
		}
		return {
			scope: { selectAll: true, ...(hasActiveLeadFilters(filters) ? { filters } : {}) },
			count: totalCount,
			knownLeads: leads,
			prospectQueryId: selectedSearchQueryId,
		};
	}, [selectedLeads, selectAllInFilter, filters, totalCount, leads, selectedSearchQueryId]);
};
